import { tokens } from '../theme/generated/tokens';

const LEGACY_COLOR_PATH_REGEX =
  /^(blue|gray|grey|green|aqua|indigo|red|yellow)\.(\d+)$/i;

/**
 * Maps a legacy MUI palette color path to a semantic `theme.data` token.
 *
 * Use only for colors that represent data (chart series, data indicators,
 * metric markers). Do NOT use for structural UI chrome — backgrounds,
 * borders, text colors — use structural tokens directly for those.
 *
 * Accepts three input shapes:
 * - A `var(--...)` CSS variable string → returned as-is (already semantic).
 * - A legacy palette path like `'indigo.500'` → mapped to the matching
 *   `tokens.theme.data.indigo500` token.
 * - Anything else (or an unmapped shade) → returns the `fallback`.
 */
export const mapPaletteColorToDataColorToken = (
  value: string | undefined,
  fallback: string
): string => {
  if (!value || typeof value !== 'string') {
    return fallback;
  }

  // Already a semantic CSS variable — pass through untouched.
  if (value.startsWith('var')) {
    return value;
  }

  const match = value.match(LEGACY_COLOR_PATH_REGEX);

  if (!match) {
    return fallback;
  }

  const colorName = match[1];
  const shade = match[2];
  const normalizedColorName = colorName === 'gray' ? 'grey' : colorName;
  const tokenKey =
    `${normalizedColorName}${shade}` as keyof typeof tokens.theme.data;
  const tokenValue = tokens.theme.data[tokenKey];
  return tokenValue ?? fallback;
};
